/**
 * Heat Loss Calculator
 * ===
 * Main application view.
 * 
 * Extends Backbone View.
 *
 * Author: Duncan McMillan
 *
 * Copyright (c) August 2024 · Art & Soul Communications Ltd.
 * https://artandsoul.co.uk
 *
 */
'use strict';

import _ from 'underscore';
import Backbone from 'backbone';

import { HeatLossCalculator } from './../HeatLossCalculator.Object.js';
import { HLC_Configs } from './../HeatLossCalculator.Configs.js';

import { RangeModel } from './../backbone.models/RangeModel.js';
import { RoomModel } from './../backbone.models/RoomModel.js';

import { InputView } from './InputView.js';
import { HeatLossStatementView } from './HeatLossStatementView.js';
import { RangeSelectionView } from './RangeSelectionView.js';
import { RadiatorSuggestionsView } from './RadiatorSuggestionsView.js';

/**
 * App View
 *
 */
export var AppView = Backbone.View.extend({
    
    events: {
        'click .hlc-input-pane button' : 'modifyInput',
        'change .hlc-input-pane input' : 'modifyInput',
        'click a.hlc-t-and-c' : 'showModal',
        'click .hlc-button--reset' : function() { location.reload(); }
    },
    
    room: undefined,
    all_ranges: undefined,
    qualified_ranges: undefined,
    preferred_ranges: undefined,
    
    input_views: undefined,
    heat_loss_statement_view: undefined,
    range_selection_view: undefined,
    radiator_suggestions_view: undefined,
    
    is_room_spec_complete: undefined,
    
    scrollto_offset: undefined,
    
    initialize: function(options) {
        
        var that = this;
        
        this.room = new RoomModel();
        this.all_ranges = new Backbone.Collection(null, {
            model: RangeModel
        });
        
        // Set some configs based on HLC target market
        HLC_Configs.radiator_button_title_text = HLC_Configs.hlc_version_configs[options.range_qualifier]['radiator_button_title_text'];
        HLC_Configs.radiator_button_anchor_text = HLC_Configs.hlc_version_configs[options.range_qualifier]['radiator_button_anchor_text'];
        HLC_Configs.hlc_version = options.range_qualifier;
        
        // Qualiifed ranges is a subset of all ranges filtered by the HLC target market
        this.qualified_ranges = new Backbone.Collection(null, {
            comparator: function(range_1, range_2) {
                
                var result;
                
                switch (true) {
                    
                    case range_1.has('classification') && ! range_2.has('classification') :
                        result = -1;
                        break;
                    
                    case range_1.get('classification').position < range_2.get('classification').position :
                        result = -1;
                        break;
                    
                    case range_2.has('classification') && ! range_1.has('classification') :
                        result = 1;
                        break;
                    
                    case range_1.get('classification').position > range_2.get('classification').position :
                        result = 1;
                        break;
                    
                    default :
                        result = 0; 
                }
                
                return result;
            },
            parse: true
        });
        
        this.preferred_ranges = new Backbone.Collection();
        
        this.all_ranges.url = options.radiator_ranges_url;
        this.all_ranges.fetch({
            success: function() {
                
                that.qualified_ranges.add(that.all_ranges.filter(function(range) {
                    return range.get('status') === 'current' && range.get('series') !== undefined;
                }));
                
                that.render();
            },
            error: function(collection, response, options) {
                var message = 'There was a problem loading the data – Please refresh the page. Error ' + response.status + ' : ' + response.statusText + '.';
                window.alert(message);
            }
        });
        
        this.input_views = [];
        this.is_room_spec_complete = false;
        
        if (options.scrollto_offset) {
            this.scrollto_offset = options.scrollto_offset;
        } else {
            this.scrollto_offset = 0;
        }
        
        this.listenTo(HeatLossCalculator.vent, 'roomChange', function() {
            that.onRoomChange();
        }).listenTo(HeatLossCalculator.vent, 'rangeSelect', function(range) {
            that.onRangeSelect(range);
        }).listenTo(HeatLossCalculator.vent, 'rangeDeselect', function(range) {
            that.onRangeDeselect(range);
        }).listenTo(HeatLossCalculator.vent, 'allRangesSelect', function(ranges) {
            that.onAllRangesSelect(ranges);
        }).listenTo(HeatLossCalculator.vent, 'allRangesDeselect', function() {
            that.onAllRangesDeselect();
        });
        
        $('#hlc-terms-and-conditions article').click(function(e) {
            e.stopPropagation();
        });
        
        $('#hlc-terms-and-conditions .hlc-button, #hlc-terms-and-conditions .overlay-content').click(function(e) {
            that.hideModal(e);
        });
        
        return this;
    },
    
    onRangeSelect: function(range) {
        this.preferred_ranges.add(range);
        this.radiator_suggestions_view.reinstateRejectsFromRange(range).render().activate();
    },
    
    onRangeDeselect: function(range) {
        this.preferred_ranges.remove(range);
        this.radiator_suggestions_view.render();
    },
    
    onAllRangesSelect: function(ranges) {
        this.preferred_ranges.reset(ranges.models);
        this.radiator_suggestions_view.reinstateRejects().render().activate();
    },
    
    onAllRangesDeselect: function() {
        this.preferred_ranges.reset([]);
        this.radiator_suggestions_view.render();
    },
    
    onRoomChange: function() {
        
        var changed = Object.keys(this.room.changedAttributes());
        
        if (changed.includes('mwt') || changed.includes('temperature')) {
            this.$el.find('.hlc-delta-t-value').text(this.room.deltaT());
        }
        
        this.radiator_suggestions_view.render();
        this.heat_loss_statement_view.render();
    },
    
    modifyInput: function(e) {
        
        var that, attributes, property, value, step_index, scroll_to;
        
        that = this;
        
        attributes = {};
        
        property = $(e.currentTarget).attr('name');
        value = $(e.currentTarget).val();
        value = _.isFinite(value) ? Number(value) : value;
        
        step_index = $(e.currentTarget).parents('.hlc-input-pane').data('step');
        
        switch (property) {
            
            case 'room_type' :
                attributes = HLC_Configs[property + 's'][value];
                break;
                
            case 'external_wall_type' :
            case 'floor_type' :
            case 'ceiling_type' :
                attributes[property] = HLC_Configs[property + 's'][value];
                break;
                
            case 'glazing_type' :
                // Obviate the requirement for the total_glazed_area input
                // if 'No external windows or doors' button is selected
                if (value === 'none') {
                    this.$el.find('input[name="total_glazed_area"]').addClass('is-exempt').val(0).prop('disabled', true);
                    attributes.glazing_type = HLC_Configs.null_glazing_type;
                    attributes.total_glazed_area = 0;
                } else {
                    this.$el.find('input[name="total_glazed_area"]').removeClass('is-exempt').prop('disabled', false);
                    attributes.glazing_type = HLC_Configs[property + 's'][value];
                }
                
                break;
                
            case 'heating_system_type' :
                
                // Dump any range selections cos they’ll no longer apply
                this.onRangeDeselect();
                
                if (value === 'hydronic') {
                    this.$el.find('.hlc-section__conditional-parameters').fadeIn({
                        start: function () {
                            $(this).css({
                                display: 'flex'
                            });
                            $(this).find('.hlc-delta-t-value').text(that.room.deltaT());
                        }
                    });
                } else {
                    this.$el.find('.hlc-section__conditional-parameters').fadeOut();
                }
                
                attributes[property] = value;
                break;
                
            default :
                attributes[property] = value;
        }
        
        this.room.set(attributes);
        
        switch (true) {
            
            case step_index + 1 === this.input_views.length && this.input_views[step_index].isComplete():
                
                this.is_room_spec_complete = true;
                
                // No need to call isComplete on last step if 
                // it only contains a single set of buttons.
                this.input_views[step_index].deactivate();
                
                this.heat_loss_statement_view.$el.fadeIn();
                
                this.range_selection_view.render().activate();
                
                break;
                
            case this.input_views[step_index].isComplete() :
                
                this.input_views[step_index].deactivate();
                
                if ( this.input_views[step_index + 1].$el.hasClass('is-inactive') ) {
                    this.input_views[step_index + 1].activate();
                    
                    scroll_to = this.input_views[step_index + 1].$el.offset().top - this.scrollto_offset;
                    
                    $('html, body').animate({
                        scrollTop: scroll_to
                    });
                }
                
                // If “No external walls” is selected we’ll skip to the next step
                if ( property === 'external_wall_count' && value === 0 && this.input_views[step_index + 2].$el.hasClass('is-inactive') ) {
                    this.input_views[step_index + 2].activate();
                }
                
                break;
                
            default :
                // Do nothing
        }
        
    },
    
    render: function() {
        
        var that = this;
        
        _.each(this.input_views, function(input_view) {
            input_view.remove();
        });
        
        if (this.range_selection_view) {
            this.range_selection_view.remove();
        }
        
        if (this.radiators_view) {
            this.radiators_view.remove();
        }
        
        while(this.input_views.length < 8) {
            
            var input_view, id_index;
            
            id_index = this.input_views.length + 1;
            
            input_view = new InputView({
                id: 'step-' + id_index + '-pane',
                template: $('#template-step-' + id_index).html(),
                model: this.room,
                step_index: (id_index - 1)
            });
            
            this.input_views.push(input_view);
            this.$el.append(input_view.render().el);
        }
        
        this.heat_loss_statement_view = new HeatLossStatementView({
            id: 'step-8a-heat-loss-statement',
            template: $('#template-heat-loss-statement').html(),
            model: this.room
        });
        
        this.$el.append(this.heat_loss_statement_view.render().el);
        
        // Render the Range Selection view
        this.range_selection_view = new RangeSelectionView({
            id: 'step-9-radiator-ranges-pane',
            template: $('#template-range-selection').html(),
            model: this.room,
            collection: this.qualified_ranges
        });
        
        this.$el.append(this.range_selection_view.render().el);
        
        // Render the Radiator Suggestions view
        this.radiator_suggestions_view = new RadiatorSuggestionsView({
            id: 'step-10-radiators-pane',
            template: $('#template-radiator-suggestions').html(),
            model: this.room
        });
        
        this.$el.append(this.radiator_suggestions_view.render().el);
        
        this.input_views[0].activate();
        
        return this;
    },
    
    showModal: function(e) {
        e.preventDefault();
        $('#hlc-terms-and-conditions').fadeIn();
    },
    
    hideModal: function(e) {
        e.preventDefault();
        $('#hlc-terms-and-conditions').fadeOut();
    }
    
});
